export type SpringConfig = {
    stiffness: number;
    damping: number;
    mass: number;
};

export const SPRING_PRESETS = {
    zoom: { stiffness: 300, damping: 30, mass: 1 },
    pan: { stiffness: 170, damping: 26, mass: 1 },
    bounceBack: { stiffness: 400, damping: 35, mass: 1 },
    doubleTap: { stiffness: 200, damping: 22, mass: 1 }
} as const satisfies Record<string, SpringConfig>;

export class Spring1D {
    position: number;
    velocity = 0;
    target: number;

    stiffness: number;
    damping: number;
    mass: number;

    constructor(initial: number, config: SpringConfig = SPRING_PRESETS.pan) {
        this.position = initial;
        this.target = initial;
        this.stiffness = config.stiffness;
        this.damping = config.damping;
        this.mass = config.mass;
    }

    update(dt: number): void {
        // Cap dt to prevent instability after tab switches
        const clampedDt = Math.min(dt, 1 / 30);
        const displacement = this.position - this.target;
        const springForce = -this.stiffness * displacement;
        const dampingForce = -this.damping * this.velocity;
        const acceleration = (springForce + dampingForce) / this.mass;

        // Semi-implicit Euler: update velocity first, then position
        this.velocity += acceleration * clampedDt;
        this.position += this.velocity * clampedDt;
    }

    isAtRest(threshold = 0.01): boolean {
        return Math.abs(this.velocity) < threshold && Math.abs(this.position - this.target) < threshold;
    }

    snapTo(value: number): void {
        this.position = value;
        this.target = value;
        this.velocity = 0;
    }

    setTarget(target: number, velocity?: number): void {
        this.target = target;
        if (velocity !== undefined) {
            this.velocity = velocity;
        }
    }
}

export class SpringAnimator {
    private springs = new Map<string, Spring1D>();
    private running = false;
    private lastTime = 0;
    private rafId = 0;
    private onUpdate: () => void;

    constructor(onUpdate: () => void) {
        this.onUpdate = onUpdate;
    }

    addSpring(name: string, spring: Spring1D): void {
        this.springs.set(name, spring);
    }

    getSpring(name: string): Spring1D | undefined {
        return this.springs.get(name);
    }

    getValue(name: string): number {
        return this.springs.get(name)?.position ?? 0;
    }

    start(): void {
        if (this.running) return;
        this.running = true;
        this.lastTime = performance.now();
        this.rafId = requestAnimationFrame(this.tick);
    }

    private tick = (now: number): void => {
        if (!this.running) return;

        const dt = (now - this.lastTime) / 1000;
        this.lastTime = now;

        let allAtRest = true;
        for (const spring of this.springs.values()) {
            spring.update(dt);
            if (!spring.isAtRest()) allAtRest = false;
        }

        this.onUpdate();

        if (allAtRest) {
            this.running = false;
            // Snap all springs to their targets
            for (const spring of this.springs.values()) {
                spring.position = spring.target;
                spring.velocity = 0;
            }
            this.onUpdate();
        } else {
            this.rafId = requestAnimationFrame(this.tick);
        }
    };

    isAnimating(): boolean {
        return this.running;
    }

    stop(): void {
        this.running = false;
        cancelAnimationFrame(this.rafId);
    }

    destroy(): void {
        this.stop();
        this.springs.clear();
    }
}
